/**
 * 📝 [Phase 102 — 2026-07-09] Éditeur WYSIWYG Tiptap pour contrats de travail.
 *
 * Rôle
 * ────
 * Éditeur riche (gras, italique, listes, titres, tableau, alignement) avec une
 * palette de **placeholders cliquables** regroupés par catégorie. Chaque chip
 * insère un placeholder `{{VAR}}` à la position du curseur — le backend
 * (`services/contract_pdf.py`) remplace ensuite ces placeholders lors du
 * rendu PDF.
 *
 * Contraintes
 * ──────────
 * - Rétrocompat : si le contenu injecté est en texte brut avec `{placeholder}`
 *   (simple accolade des 5 templates actuels), Tiptap l'affiche tel quel via
 *   `<p>` — le rendu PDF continue de fonctionner car le service PDF détecte
 *   automatiquement HTML vs texte brut.
 * - FR-CA partout, y compris libellés des chips.
 */
import { useEffect, useMemo } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Undo,
  Redo,
  Type,
} from "lucide-react";

// 📋 Chips regroupées par catégorie. La clé est utilisée par le backend pour
//     remplacer `{{KEY}}` par la valeur réelle lors du rendu PDF.
export const PHASE102_PLACEHOLDER_GROUPS = [
  {
    label: "Employé",
    chips: [
      { key: "NOM_EMPLOYE", label: "Nom de l'employé" },
      { key: "PRENOM_EMPLOYE", label: "Prénom de l'employé" },
      { key: "NOM_FAMILLE_EMPLOYE", label: "Nom de famille" },
      { key: "EMAIL_EMPLOYE", label: "Courriel" },
      { key: "TELEPHONE_EMPLOYE", label: "Téléphone" },
      { key: "ADRESSE_EMPLOYE", label: "Adresse" },
    ],
  },
  {
    label: "Poste",
    chips: [
      { key: "POSTE", label: "Poste" },
      { key: "TYPE_EMPLOI", label: "Type d'emploi" },
      { key: "DATE_EMBAUCHE", label: "Date d'embauche" },
      { key: "SALAIRE_HORAIRE", label: "Salaire horaire" },
      { key: "SALAIRE_ANNUEL", label: "Salaire annuel calculé" },
    ],
  },
  {
    label: "Employeur",
    chips: [
      { key: "EMPLOYEUR_NOM", label: "Nom de l'employeur" },
      { key: "EMPLOYEUR_SIGNATURE", label: "Signature de l'employeur" },
    ],
  },
  {
    label: "Signature",
    chips: [
      { key: "DATE_SIGNATURE", label: "Date de signature" },
      { key: "SIGNATURE_NOM", label: "Nom saisi à la signature" },
    ],
  },
];

const CATEGORY_COLORS = {
  "Employé": "bg-blue-50 border-blue-300 text-blue-800 hover:bg-blue-100",
  "Poste": "bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100",
  "Employeur": "bg-purple-50 border-purple-300 text-purple-800 hover:bg-purple-100",
  "Signature": "bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100",
};

function ToolbarButton({ onClick, active, disabled, children, label, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      data-testid={testId}
      className={`p-1.5 rounded border text-xs transition-colors ${
        active
          ? "bg-slate-800 text-white border-slate-800"
          : "bg-white text-slate-700 border-slate-200 hover:bg-slate-100"
      } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
    >
      {children}
    </button>
  );
}

/**
 * TiptapContractEditor
 *
 * Props
 * ─────
 * @param {string} value        HTML string courant du template (peut être du texte brut au 1er chargement)
 * @param {(html: string) => void} onChange  Callback à chaque édition
 * @param {string} testIdPrefix Préfixe pour les data-testid (défaut : "contrat-editor")
 */
export function TiptapContractEditor({
  value,
  onChange,
  testIdPrefix = "contrat-editor",
}) {
  const editor = useEditor({
    extensions: [StarterKit],
    content: value || "",
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      onChange?.(html);
    },
    editorProps: {
      attributes: {
        class:
          "prose prose-sm max-w-none min-h-[400px] p-4 focus:outline-none border border-slate-200 rounded-b-lg bg-white",
        "data-testid": `${testIdPrefix}-content`,
      },
    },
  });

  // Sync externe (ex: switch entre types de contrats)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || "") !== current) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  const insertPlaceholder = (key) => {
    if (!editor) return;
    const token = `{{${key}}}`;
    editor.chain().focus().insertContent(token).run();
  };

  const charCount = useMemo(() => {
    if (!editor) return 0;
    return editor.getText().length;
  }, [editor, value]);

  if (!editor) {
    return (
      <div className="border border-slate-200 rounded-lg p-4 bg-slate-50 text-sm text-slate-500">
        Chargement de l&apos;éditeur…
      </div>
    );
  }

  return (
    <div data-testid={`${testIdPrefix}-root`}>
      {/* Palette de placeholders (chips par catégorie) */}
      <div
        className="border border-slate-200 rounded-t-lg bg-slate-50 p-3 space-y-2"
        data-testid={`${testIdPrefix}-placeholders`}
      >
        <p className="text-xs font-medium text-slate-600 flex items-center gap-1">
          <Type className="w-3 h-3" /> Insérer une variable dynamique
        </p>
        {PHASE102_PLACEHOLDER_GROUPS.map((group) => (
          <div key={group.label} className="flex items-start gap-2 flex-wrap">
            <Badge
              variant="outline"
              className="text-[10px] uppercase tracking-wider bg-white shrink-0 mt-0.5"
              data-testid={`${testIdPrefix}-group-${group.label}`}
            >
              {group.label}
            </Badge>
            <div className="flex flex-wrap gap-1.5">
              {group.chips.map((chip) => (
                <button
                  key={chip.key}
                  type="button"
                  onClick={() => insertPlaceholder(chip.key)}
                  className={`px-2 py-0.5 rounded-md border text-xs font-medium transition-colors ${
                    CATEGORY_COLORS[group.label] ||
                    "bg-slate-50 border-slate-300 text-slate-700 hover:bg-slate-100"
                  }`}
                  title={`Insérer {{${chip.key}}}`}
                  data-testid={`${testIdPrefix}-chip-${chip.key}`}
                >
                  {chip.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar formatage */}
      <div
        className="border-x border-slate-200 bg-white p-2 flex flex-wrap gap-1 items-center"
        data-testid={`${testIdPrefix}-toolbar`}
      >
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive("bold")}
          label="Gras"
          testId={`${testIdPrefix}-btn-bold`}
        >
          <Bold className="w-3.5 h-3.5" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive("italic")}
          label="Italique"
          testId={`${testIdPrefix}-btn-italic`}
        >
          <Italic className="w-3.5 h-3.5" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          active={editor.isActive("strike")}
          label="Barré"
          testId={`${testIdPrefix}-btn-strike`}
        >
          <Underline className="w-3.5 h-3.5" />
        </ToolbarButton>
        <div className="w-px h-5 bg-slate-200 mx-1" />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          active={editor.isActive("heading", { level: 1 })}
          label="Titre 1"
          testId={`${testIdPrefix}-btn-h1`}
        >
          <Heading1 className="w-3.5 h-3.5" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          active={editor.isActive("heading", { level: 2 })}
          label="Titre 2"
          testId={`${testIdPrefix}-btn-h2`}
        >
          <Heading2 className="w-3.5 h-3.5" />
        </ToolbarButton>
        <div className="w-px h-5 bg-slate-200 mx-1" />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive("bulletList")}
          label="Liste à puces"
          testId={`${testIdPrefix}-btn-bullet`}
        >
          <List className="w-3.5 h-3.5" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive("orderedList")}
          label="Liste numérotée"
          testId={`${testIdPrefix}-btn-ordered`}
        >
          <ListOrdered className="w-3.5 h-3.5" />
        </ToolbarButton>
        <div className="w-px h-5 bg-slate-200 mx-1" />
        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          label="Annuler"
          testId={`${testIdPrefix}-btn-undo`}
        >
          <Undo className="w-3.5 h-3.5" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          label="Rétablir"
          testId={`${testIdPrefix}-btn-redo`}
        >
          <Redo className="w-3.5 h-3.5" />
        </ToolbarButton>
      </div>

      {/* Éditeur */}
      <EditorContent editor={editor} />

      <p className="text-xs text-slate-500 mt-1" data-testid={`${testIdPrefix}-count`}>
        {charCount} caractère{charCount > 1 ? "s" : ""}
      </p>
    </div>
  );
}

export default TiptapContractEditor;
